import React, { useEffect, useState, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import arsClaimService, { type ArsClaim, type ClaimStatus, type ClaimFilters } from '../../../services/arsClaimService';
import ClaimStatusBadge from '../components/ClaimStatusBadge';
import { insuranceCompanyService } from '../../../services/insuranceService';
import PermissionGate from '../../../shared/components/PermissionGate';
import Breadcrumb from '../../../components/ui/Breadcrumb';
import { formatCurrency } from '../../../lib/format';
import { parseApiDate } from '../../../lib/format';

const ClaimListPage: React.FC = () => {
    const navigate = useNavigate();
    const [claims, setClaims] = useState<ArsClaim[]>([]);
    const [loading, setLoading] = useState(true);
    const [total, setTotal] = useState(0);
    const [page, setPage] = useState(1);
    const [search, setSearch] = useState('');
    const [searchInput, setSearchInput] = useState('');
    const [statusFilter, setStatusFilter] = useState<ClaimStatus | ''>('');
    const [companyFilter, setCompanyFilter] = useState<number | ''>('');
    const [companies, setCompanies] = useState<Array<{ id: number; name: string }>>([]);
    const [dateFrom, setDateFrom] = useState('');
    const [dateTo, setDateTo] = useState('');
    const [downloading, setDownloading] = useState<Record<number, boolean>>({});

    const perPage = 15;

    const fetchCompanies = useCallback(async () => {
        try {
            const res = await insuranceCompanyService.getAll({ per_page: 100 });
            setCompanies(res.data.map(c => ({ id: c.id, name: c.name })));
        } catch {
            // ignore
        }
    }, []);

    const fetchData = useCallback(async () => {
        setLoading(true);
        try {
            const filters: ClaimFilters = {
                page,
                per_page: perPage,
                search: search || undefined,
                status: statusFilter || undefined,
                company_id: companyFilter || undefined,
                date_from: dateFrom || undefined,
                date_to: dateTo || undefined,
            };
            const res = await arsClaimService.getAll(filters);
            setClaims(res.data);
            setTotal(res.total);
        } catch {
            setClaims([]);
        } finally {
            setLoading(false);
        }
    }, [page, search, statusFilter, companyFilter, dateFrom, dateTo]);

    useEffect(() => {
        fetchCompanies();
    }, [fetchCompanies]);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    const handleSearch = () => {
        setPage(1);
        setSearch(searchInput);
    };

    const handleDownloadSisalril = async (id: number) => {
        setDownloading(prev => ({ ...prev, [id]: true }));
        try {
            const res = await arsClaimService.generateSisalril(id);
            const blob = new Blob([res.data.content], { type: 'text/plain;charset=Windows-1252' });
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = res.data.filename;
            a.click();
            URL.revokeObjectURL(url);
        } catch {
            alert('Error al descargar archivo SISALRIL');
        } finally {
            setDownloading(prev => ({ ...prev, [id]: false }));
        }
    };

    const formatDate = (d: string | null) => {
        if (!d) return '-';
        return (parseApiDate(d) ?? new Date(NaN)).toLocaleDateString('es-DO', { day: '2-digit', month: 'short', year: 'numeric' });
    };

    const totalPages = Math.ceil(total / perPage);

    return (
        <>
            <div className="page-header">
                <div className="row align-items-center">
                    <div className="col-sm-6">
                        <Breadcrumb items={[{ label: 'Inicio', to: '/' }, { label: 'Reclamaciones ARS' }]} />
                    </div>
                    <div className="col-sm-6 text-end">
                        <PermissionGate module="insurance" action="create">
                            <Link to="/claims/new" className="btn btn-primary">
                                <i className="fa fa-plus me-1" /> Nueva Reclamación
                            </Link>
                        </PermissionGate>
                    </div>
                </div>
            </div>

            <div className="row">
                <div className="col-sm-12">
                    <div className="card">
                        <div className="card-body">
                            {/* Filters */}
                            <div className="row mb-3 g-2">
                                <div className="col-md-3">
                                    <div className="input-group">
                                        <input
                                            type="text"
                                            className="form-control"
                                            placeholder="Buscar por # o paciente..."
                                            value={searchInput}
                                            onChange={e => setSearchInput(e.target.value)}
                                            onKeyDown={e => e.key === 'Enter' && handleSearch()}
                                        />
                                        <button className="btn btn-outline-secondary" onClick={handleSearch} aria-label="Buscar">
                                            <i className="fa fa-search" />
                                        </button>
                                    </div>
                                </div>
                                <div className="col-md-2">
                                    <select
                                        className="form-select"
                                        value={statusFilter}
                                        onChange={e => { setStatusFilter(e.target.value as ClaimStatus | ''); setPage(1); }}
                                    >
                                        <option value="">Todos los estados</option>
                                        <option value="draft">Borrador</option>
                                        <option value="submitted">Enviado</option>
                                        <option value="accepted">Aceptado</option>
                                        <option value="partially_accepted">Parcial</option>
                                        <option value="rejected">Rechazado</option>
                                        <option value="cancelled">Cancelado</option>
                                    </select>
                                </div>
                                <div className="col-md-2">
                                    <select
                                        className="form-select"
                                        value={companyFilter}
                                        onChange={e => { setCompanyFilter(e.target.value as number | ''); setPage(1); }}
                                    >
                                        <option value="">Todas las ARS</option>
                                        {companies.map(c => (
                                            <option key={c.id} value={c.id}>{c.name}</option>
                                        ))}
                                    </select>
                                </div>
                                <div className="col-md-2">
                                    <input
                                        type="date"
                                        className="form-control"
                                        value={dateFrom}
                                        onChange={e => { setDateFrom(e.target.value); setPage(1); }}
                                        placeholder="Desde"
                                    />
                                </div>
                                <div className="col-md-2">
                                    <input
                                        type="date"
                                        className="form-control"
                                        value={dateTo}
                                        onChange={e => { setDateTo(e.target.value); setPage(1); }}
                                        placeholder="Hasta"
                                    />
                                </div>
                                <div className="col-md-1">
                                    <button className="btn btn-outline-danger btn-sm" onClick={() => {
                                        setSearchInput('');
                                        setSearch('');
                                        setStatusFilter('');
                                        setCompanyFilter('');
                                        setDateFrom('');
                                        setDateTo('');
                                        setPage(1);
                                    }} aria-label="Limpiar filtros">
                                        <i className="fa fa-eraser" />
                                    </button>
                                </div>
                            </div>

                            {/* Table */}
                            {loading ? (
                                <div className="text-center py-4">
                                    <div className="spinner-border text-primary" role="status">
                                        <span className="visually-hidden">Cargando...</span>
                                    </div>
                                </div>
                            ) : claims.length === 0 ? (
                                <div className="text-center py-4 text-muted">
                                    <i className="fa fa-file-invoice fa-3x mb-3 d-block" />
                                    <p>No se encontraron reclamaciones.</p>
                                </div>
                            ) : (
                                <div className="table-responsive">
                                    <table className="table table-hover">
                                        <thead>
                                            <tr>
                                                <th># Reclamación</th>
                                                <th>Paciente</th>
                                                <th>ARS</th>
                                                <th>Monto Total</th>
                                                <th>Estado</th>
                                                <th>Fecha Envío</th>
                                                <th>Acciones</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {claims.map(claim => (
                                                <tr key={claim.id} style={{ cursor: 'pointer' }} onClick={() => navigate(`/claims/${claim.id}`)}>
                                                    <td><code>{claim.claim_number}</code></td>
                                                    <td>
                                                        {claim.patient
                                                            ? `${claim.patient.first_name} ${claim.patient.last_name}`
                                                            : `Paciente #${claim.patient_id}`}
                                                    </td>
                                                    <td>{claim.insurance_company?.name ?? '-'}</td>
                                                    <td>{formatCurrency(claim.total_amount)}</td>
                                                    <td><ClaimStatusBadge status={claim.status} /></td>
                                                    <td>{formatDate(claim.submission_date)}</td>
                                                    <td onClick={e => e.stopPropagation()}>
                                                        <div className="d-flex gap-1">
                                                            <Link to={`/claims/${claim.id}`} className="action-btn action-btn-view" title="Ver">
                                                                <i className="fa fa-eye" />
                                                            </Link>
                                                            <PermissionGate module="insurance" action="submit">
                                                                {claim.status === 'draft' && (
                                                                    <button
                                                                        className="btn btn-sm btn-primary"
                                                                        title="Enviar"
                                                                        onClick={async () => {
                                                                            try {
                                                                                await arsClaimService.submitClaim(claim.id);
                                                                                fetchData();
                                                                            } catch { alert('Error al enviar'); }
                                                                        }}
                                                                    >
                                                                        <i className="fa fa-paper-plane" />
                                                                    </button>
                                                                )}
                                                            </PermissionGate>
                                                            <PermissionGate module="insurance" action="export">
                                                                {(claim.status === 'submitted' || claim.status === 'accepted' || claim.status === 'partially_accepted') && (
                                                                    <button
                                                                        className="btn btn-sm btn-secondary"
                                                                        title="Descargar SISALRIL"
                                                                        disabled={downloading[claim.id]}
                                                                        onClick={() => handleDownloadSisalril(claim.id)}
                                                                    >
                                                                        <i className="fa fa-download" />
                                                                    </button>
                                                                )}
                                                            </PermissionGate>
                                                        </div>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            )}

                            {/* Pagination */}
                            {totalPages > 1 && (
                                <nav className="mt-3">
                                    <ul className="pagination justify-content-center mb-0">
                                        <li className={`page-item ${page <= 1 ? 'disabled' : ''}`}>
                                            <button className="page-link" onClick={() => setPage(p => Math.max(1, p - 1))}>
                                                <i className="fa fa-chevron-left" />
                                            </button>
                                        </li>
                                        {Array.from({ length: totalPages }, (_, i) => i + 1).map(p => (
                                            <li key={p} className={`page-item ${p === page ? 'active' : ''}`}>
                                                <button className="page-link" onClick={() => setPage(p)}>{p}</button>
                                            </li>
                                        ))}
                                        <li className={`page-item ${page >= totalPages ? 'disabled' : ''}`}>
                                            <button className="page-link" onClick={() => setPage(p => Math.min(totalPages, p + 1))}>
                                                <i className="fa fa-chevron-right" />
                                            </button>
                                        </li>
                                    </ul>
                                </nav>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
};

export default ClaimListPage;
